import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { DURATION, EASE_IN, EASE_OUT } from '../tokens/motion'
import { cn } from '../lib/cn'

/**
 * Every open dialog, in the order it rose. Escape belongs to the one on
 * top — a dialog opened over a dialog must close alone, never take its
 * parent down with it.
 */
const stack: { id: symbol; close: () => void }[] = []

/**
 * A floating window over the page: the background dims and holds still, and
 * the card arrives — rising a breath, settling to true — and leaves the way
 * it came. Things that appear or vanish use a duration and an ease, never a
 * spring; the rounding is generous, the shadow modal, the exit unhurried
 * enough to be seen leaving. Escape and the scrim both close it, and focus
 * returns to whatever opened it.
 *
 * The card itself is the caller's to compose — width, padding, a two-panel
 * layout — through `className`; the primitive owns only the arrival.
 */
export function Dialog({
  open,
  onClose,
  children,
  labelledBy,
  className,
}: {
  open: boolean
  onClose: () => void
  children: ReactNode
  /** id of the element naming this window, for screen readers. */
  labelledBy?: string
  /** Extra classes on the card: width, padding, layout. */
  className?: string
}) {
  const card = useRef<HTMLDivElement>(null)
  const close = useRef(onClose)
  useEffect(() => {
    close.current = onClose
  })

  useEffect(() => {
    if (!open) return
    const opener = document.activeElement as HTMLElement | null
    const entry = { id: Symbol('dialog'), close: () => close.current() }
    stack.push(entry)
    const onKey = (event: KeyboardEvent) => {
      // A field popover receives Escape before the enclosing surface.
      if (event.defaultPrevented || event.target instanceof Element && event.target.closest('[data-wf-popover]')) return
      if (event.key !== 'Escape') return
      // Only the topmost floating window answers; the ones beneath hold.
      if (stack[stack.length - 1] !== entry) return
      event.preventDefault()
      close.current()
    }
    window.addEventListener('keydown', onKey, true)
    card.current?.focus()
    return () => {
      window.removeEventListener('keydown', onKey, true)
      const at = stack.indexOf(entry)
      if (at !== -1) stack.splice(at, 1)
      opener?.focus?.()
    }
  }, [open])

  return createPortal(
    <AnimatePresence>
      {open ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
          <motion.div
            className="wf-scrim absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: DURATION.base, ease: EASE_OUT } }}
            exit={{ opacity: 0, transition: { duration: DURATION.base, ease: EASE_IN } }}
            onClick={onClose}
          />
          <motion.div
            ref={card}
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledBy}
            tabIndex={-1}
            className={cn('relative my-auto w-full outline-none', className)}
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0, transition: { duration: DURATION.slow, ease: EASE_OUT } }}
            exit={{ opacity: 0, scale: 0.98, y: 6, transition: { duration: DURATION.base, ease: EASE_IN } }}
          >
            {children}
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>,
    document.body,
  )
}
